import React, { useEffect, useState } from "react";
import { Trash2, MapPin, Camera, Upload, Clock, User, MessageSquare, Send, Loader2 } from "lucide-react";
import UploadStateBadge from "@/components/UploadStateBadge";
import { removeOp } from "@/lib/offlineQueue";
import api from "@/lib/api";
import { PHASES } from "@/lib/constants";
import AuthImage from "@/components/AuthImage";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const fmt = (iso) => (iso || "").slice(0, 16).replace("T", " ");

export function PhotoDialog({ photo, onClose, onChanged, allowPhase }) {
  const [desc, setDesc] = useState(photo?.description || "");
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (photo) api.get(`/files/${photo.id}/comments`).then((r) => setComments(r.data)); }, [photo]);
  if (!photo) return null;
  const patch = async (p) => { await api.patch(`/files/${photo.id}`, p); onChanged && onChanged(); };
  const addComment = async (e) => {
    e.preventDefault(); if (!text.trim()) return; setBusy(true);
    try { const r = await api.post(`/files/${photo.id}/comments`, { text }); setComments([...comments, r.data]); setText(""); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-auto" data-testid="photo-dialog">
        <DialogHeader><DialogTitle className="font-head text-base truncate">{photo.original_filename}</DialogTitle></DialogHeader>
        <div className="grid md:grid-cols-5 gap-4">
          <div className="md:col-span-3"><AuthImage fileId={photo.id} className="w-full rounded-lg border object-contain max-h-[60vh] bg-slate-50" /></div>
          <div className="md:col-span-2 space-y-3 text-sm">
            <div className="space-y-1 text-xs text-slate-500">
              <div className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{fmt(photo.taken_at || photo.created_at)}</div>
              <div className="flex items-center gap-1.5"><User className="h-3.5 w-3.5" />{photo.uploaded_by || "—"}</div>
              <div className="flex items-center gap-1.5">{photo.capture_type === "kamera" ? <Camera className="h-3.5 w-3.5" /> : <Upload className="h-3.5 w-3.5" />}{photo.capture_type === "kamera" ? "Kameraaufnahme" : "Datei-Upload"}{photo.timestamped && " · Zeitstempel"}</div>
              {photo.lat != null && <a className="flex items-center gap-1.5 text-sky-600 hover:underline" target="_blank" rel="noreferrer" href={`https://www.openstreetmap.org/?mlat=${photo.lat}&mlon=${photo.lng}#map=18/${photo.lat}/${photo.lng}`}><MapPin className="h-3.5 w-3.5" />{photo.lat.toFixed(5)}, {photo.lng.toFixed(5)}</a>}
            </div>
            <div><label className="text-xs text-slate-500">Beschreibung</label>
              <Input value={desc} onChange={(e) => setDesc(e.target.value)} onBlur={() => desc !== photo.description && patch({ description: desc })} className="h-8 mt-1" data-testid="photo-description-input" /></div>
            {allowPhase && (
              <div><label className="text-xs text-slate-500">Vorher / Nachher</label>
                <Select value={photo.phase || "none"} onValueChange={(v) => patch({ phase: v === "none" ? "" : v })}>
                  <SelectTrigger className="h-8 mt-1" data-testid="photo-phase-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{PHASES.map((p) => <SelectItem key={p.value || "none"} value={p.value || "none"}>{p.label}</SelectItem>)}</SelectContent>
                </Select></div>
            )}
            <div className="border-t pt-3">
              <div className="text-xs font-semibold text-slate-600 flex items-center gap-1 mb-2"><MessageSquare className="h-3.5 w-3.5" />Kommentare ({comments.length})</div>
              <div className="space-y-2 max-h-40 overflow-auto scroll-thin">
                {comments.map((c) => <div key={c.id} className="bg-slate-100 rounded-lg px-2.5 py-1.5 text-xs" data-testid={`photo-comment-${c.id}`}><div>{c.text}</div><div className="text-slate-400 mt-0.5">{c.author} · {fmt(c.created_at)}</div></div>)}
              </div>
              <form onSubmit={addComment} className="flex gap-1.5 mt-2">
                <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Kommentar…" className="h-8" data-testid="photo-comment-input" />
                <Button type="submit" size="sm" className="h-8" disabled={busy} data-testid="photo-comment-submit">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}</Button>
              </form>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function MediaGrid({ photos, onChanged, allowPhase = false, cols = "grid-cols-3", size = "aspect-square" }) {
  const [active, setActive] = useState(null);
  const del = async (e, id) => {
    e.stopPropagation();
    if (String(id).startsWith("op_")) {
      const f = photos.find((p) => p.id === id);
      if (f?.__opId && window.confirm("Dieses noch nicht übertragene Foto verwerfen?")) { await removeOp(f.__opId); window.dispatchEvent(new Event("offline-queued")); }
      onChanged && onChanged(); return;
    }
    await api.delete(`/files/${id}`); onChanged && onChanged();
  };
  return (
    <>
      <div className={`grid ${cols} gap-2`}>
        {photos.map((f) => (
          <div key={f.id} className="relative group cursor-pointer" data-testid={`photo-${f.id}`} onClick={() => setActive(f)}>
            <AuthImage fileId={f.id} localUrl={f.__localUrl} thumb className={`${size} w-full rounded-lg object-cover border border-slate-200`} />
            <UploadStateBadge f={f} small />
            {f.phase && <Badge className={`absolute bottom-1 left-1 text-[9px] h-4 px-1 ${f.phase === "vorher" ? "bg-red-600" : "bg-emerald-600"}`}>{f.phase === "vorher" ? "Vorher" : "Nachher"}</Badge>}
            {f.capture_type === "kamera" && !f.__pending && <Camera className="absolute top-1 left-1 h-3.5 w-3.5 text-white drop-shadow" />}
            <button onClick={(e) => del(e, f.id)} className="absolute top-1 right-1 bg-white/90 rounded-full p-1 opacity-0 group-hover:opacity-100 text-red-500" data-testid={`delete-photo-${f.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
      </div>
      {active && <PhotoDialog photo={photos.find((p) => p.id === active.id) || active} allowPhase={allowPhase} onClose={() => setActive(null)} onChanged={onChanged} />}
    </>
  );
}
